import React, { ReactElement, useEffect, useMemo, useState, VFC } from 'react';
import { usePostHog } from 'posthog-js/react'

import { SubscriptionPaymentIntervalTypes, SubscriptionPlanObject, SubscriptionTypes } from '..';
import { DashboardSettingsBillingFeatureMapping } from '../feature-mapping';
import { DashboardSettingsBillingSubscriptionCard } from './card';
import { DashboardSettingsBillingSubscriptionCurrentPlan } from './current-plan';
import classNames from 'classnames';
import { Nullable } from 'src/types/nullable.type';
import { BillingToggle } from './billing-toggle';
import { useCurrentUser } from 'src/store/user';
import { useLocation } from 'react-router-dom';

const ContactUsCalendly = 'https://calendly.com/journey-danny/30min?month=2023-12';

interface DashboardSettingsBillingSubscriptionPlansProps {
  isSubscribed: boolean;
  isOnTrial: boolean;
  isCanceledAtPeriodEnd: boolean;
  isQuarterly?: boolean;
  isPaused: boolean;
  subscriptionPlanType: Nullable<SubscriptionTypes>;
  paymentInterval?: SubscriptionPaymentIntervalTypes;
  onUpgradeClick: (subscriptionType: SubscriptionTypes,intervalType: SubscriptionPaymentIntervalTypes) => void;
  onDowngradeToFreeClick: () => void;
  renderCurrentPlan: () => ReactElement;
  plans: SubscriptionPlanObject;
}

const STRIPE_BILLING_PORTAL_URL = 'https://billing.stripe.com/p/login/aEU2bL89lcRbc6Y288';

export const DashboardSettingsBillingSubscriptionPlans: VFC<DashboardSettingsBillingSubscriptionPlansProps> = ({
  isSubscribed,
  isPaused,
  subscriptionPlanType,
  isQuarterly,
  isOnTrial,
  isCanceledAtPeriodEnd,
  plans,
  onUpgradeClick,
  onDowngradeToFreeClick,
  paymentInterval,
  renderCurrentPlan,
}) => {
  const [activeProInterval, setActiveProInterval] = useState<SubscriptionPaymentIntervalTypes>('year');

  const posthog = usePostHog()   

  const { currentUser } = useCurrentUser();
  const location = useLocation();

  const isOnProPlan = useMemo(() => {
    return isSubscribed || isOnTrial;
  }, [isSubscribed, isOnTrial]);

  const canUpgrade = useMemo(() => {
    return !isOnProPlan || (isOnProPlan && isCanceledAtPeriodEnd);
  }, [isOnProPlan, isCanceledAtPeriodEnd]);

  const canDowngrade = useMemo(() => {
    return isOnProPlan && !isCanceledAtPeriodEnd;
  }, [isOnProPlan, isCanceledAtPeriodEnd]);

  const paymentIntervalText = useMemo(() => {
    if (isQuarterly) {
      return 'a quarterly Journey Pro subscription';
    } else if (paymentInterval === 'month') {
      if(subscriptionPlanType==='pro')
        return 'a monthly Journey Pro subscription'
      else if(subscriptionPlanType==='enterprise')
        return 'a monthly Journey Enterprise subscription'

      return 'a monthly Journey Starter subscription';
    }
    if(subscriptionPlanType==='pro')
      return 'an annual Journey Pro subscription'
    else if(subscriptionPlanType==='enterprise')
      return 'an annual Journey Enterprise subscription'
    return 'an annual Journey Starter subscription';
  }, [paymentInterval, isQuarterly]);

  const currentPlanTextByPaymentInterval = useMemo(
    () => `You're currently paying for ${paymentIntervalText}`,
    [paymentIntervalText]
  );

  const onSubcribeIntervalChange = (intervalType: SubscriptionPaymentIntervalTypes) => {
    if (activeProInterval === intervalType) {
      return;
    }
    setActiveProInterval(intervalType);
  };

  useEffect(()=>{

    try {
        const search = location.search;
        const params = new URLSearchParams(search);
        const successOpen = params.get('success');
        if (successOpen==='1') {
          if(currentUser && currentUser.uuid){
            posthog?.identify(currentUser.uuid, {
              userId: currentUser.uuid, ...(currentUser.name? {name: currentUser.name} : {}), subscription: 'paid',
          })
          }
          
          posthog?.capture('user_subscription_paid', {
            $set: {subscription_status: 'paid'},
          });
        
          posthog?.capture('pay_subscribed');
        }
      }
        catch (err) { 
          return;
      }

  },[])

  return (
    <div className='space-y-2 w-full'>
      <BillingToggle
          checked={activeProInterval==='year'}
          onChange={onSubcribeIntervalChange}
          labelType='strong'
        />

    <div className='flex space-x-4'>
      <DashboardSettingsBillingSubscriptionCard
        title='Free'
        subTitle='The basics you need to get started with Journey.'
        renderContent={() => (
          <>
            {!isOnProPlan && (
              <div className='mb-6'>
                <DashboardSettingsBillingSubscriptionCurrentPlan subTitle='This plan will be free forever' />
              </div>
            )}
            <span className='text-bedrock-p-strong'>Free forever</span>
          </>
        )}
        features={DashboardSettingsBillingFeatureMapping.FreePlan}
      />
         <DashboardSettingsBillingSubscriptionCard
        title='Starter'
        subTitle='Gives you everything to analyze and close prospects faster.'
        features={DashboardSettingsBillingFeatureMapping.StarterPlan}
        contentContainerClassName={classNames('my-6 flex items-center gap-2', {
          'flex-col': isOnProPlan,
        })}
        renderContent={() => (
          <>
            {!isOnProPlan ? (
              <div className='flex flex-col gap-1 text-bedrock-black text-bedrock-p'>
                {plans && plans.starter && plans.starter.map((plan) => {
                  const planHasQuota = !!plan.quota;

                  if (planHasQuota) {
                    return (
                      <a
                        href={ContactUsCalendly}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={classNames('cursor-pointer', {
                          'text-bedrock-p': activeProInterval !== 'quarter',
                          'text-bedrock-p-strong': activeProInterval === 'quarter',
                        })}
                        onClick={() => {
                          setActiveProInterval('quarter');
                        }}
                      >
                        {plan.label}
                      </a>
                    );
                  }

                  return (
                    <span
                      className={classNames(
                        'cursor-pointer',
                        activeProInterval === plan.payment_interval ? 'text-bedrock-p-strong' : 'opacity-80'
                      )}
                      onClick={() => {
                        onSubcribeIntervalChange(plan.payment_interval);
                      }}
                    >
                      {plan.label}
                    </span>
                  );
                })}
              </div>
            ) : 
            subscriptionPlanType==='pro' || subscriptionPlanType==='enterprise'?
            <></>
            :
            (
              <>
                <div className='mb-6'>
                  <DashboardSettingsBillingSubscriptionCurrentPlan subTitle={currentPlanTextByPaymentInterval} />
                </div>
                {renderCurrentPlan()}
              </>
            )}
          </>
        )}
        renderFooter={() => (
          <>
            {canUpgrade && (
              <div className='flex justify-center mt-auto'>
                <button
                  type='submit'
                  disabled={activeProInterval === 'quarter'}
                  className={classNames('disabled:opacity-50 btn-bedrock-primary', {
                    'pointer-events-none': activeProInterval === 'quarter',
                  })}
                  onClick={() => {
                    if (activeProInterval === 'quarter') {
                      return;
                    }
                    onUpgradeClick('starter',activeProInterval);
                  }}
                >
                  Upgrade
                </button>
              </div>
            )}
            {canDowngrade && subscriptionPlanType==='starter' &&  (
              <div className='flex justify-center mt-auto gap-2'>
                <button
                  disabled={isPaused}
                  type='submit'
                  className='btn-bedrock-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={onDowngradeToFreeClick}
                >
                  Cancel subscription
                </button>
                <a href={STRIPE_BILLING_PORTAL_URL} target='_blank' rel='noopener noreferrer'>
                  <button className='btn-bedrock-secondary'>Update billing</button>
                </a>
              </div>
            )}
          </>
        )}
      />
      <DashboardSettingsBillingSubscriptionCard
        title='Pro'
        subTitle='Gives you full control and customization over your Journeys.'
        features={DashboardSettingsBillingFeatureMapping.ProPlan}
        contentContainerClassName={classNames('my-6 flex items-center gap-2', {
          'flex-col': isOnProPlan,
        })}
        renderContent={() => (
          <>
            {!isOnProPlan ? (
              <div className='flex flex-col gap-1 text-bedrock-black text-bedrock-p'>
                {plans && plans.pro && plans.pro.map((plan) => {
                  const planHasQuota = !!plan.quota;

                  if (planHasQuota) {
                    return (
                      <a
                        href={ContactUsCalendly}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={classNames('cursor-pointer', {
                          'text-bedrock-p': activeProInterval !== 'quarter',
                          'text-bedrock-p-strong': activeProInterval === 'quarter',
                        })}
                        onClick={() => {
                          setActiveProInterval('quarter');
                        }}
                      >
                        {plan.label}
                      </a>
                    );
                  }

                  return (
                    <span
                      className={classNames(
                        'cursor-pointer',
                        activeProInterval === plan.payment_interval ? 'text-bedrock-p-strong' : 'opacity-80'
                      )}
                      onClick={() => {
                        onSubcribeIntervalChange(plan.payment_interval);
                      }}
                    >
                      {plan.label}
                    </span>
                  );
                })}
              </div>
            ) : 
            subscriptionPlanType==='starter' || subscriptionPlanType==='enterprise'?
            <></>
            :
            (
              <>
                <div className='mb-6'>
                  <DashboardSettingsBillingSubscriptionCurrentPlan subTitle={currentPlanTextByPaymentInterval} />
                </div>
                {renderCurrentPlan()}
              </>
            )}
          </>
        )}
        renderFooter={() => (
          <>
            {canUpgrade && (
              <div className='flex justify-center mt-auto'>
                <button
                  type='submit'
                  disabled={activeProInterval === 'quarter'}
                  className={classNames('disabled:opacity-50 btn-bedrock-primary', {
                    'pointer-events-none': activeProInterval === 'quarter',
                  })}
                  onClick={() => {
                    if (activeProInterval === 'quarter') {
                      return;
                    }
                    onUpgradeClick('pro',activeProInterval);
                  }}
                >
                  Upgrade
                </button>
              </div>
            )}
            {canDowngrade && subscriptionPlanType==='pro' && (
              <div className='flex justify-center mt-auto gap-2'>
                <button
                  disabled={isPaused}
                  type='submit'
                  className='btn-bedrock-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={onDowngradeToFreeClick}
                >
                  Cancel subscription
                </button>
                <a href={STRIPE_BILLING_PORTAL_URL} target='_blank' rel='noopener noreferrer'>
                  <button className='btn-bedrock-secondary'>Update billing</button>
                </a>
              </div>
            )}
          </>
        )}
      />
        <DashboardSettingsBillingSubscriptionCard
        title='Enterprise'
        subTitle='Everything you need for champion enabled success'
        features={DashboardSettingsBillingFeatureMapping.EnterprisePlan}
        contentContainerClassName={classNames('my-6 flex items-center gap-2', {
          'flex-col': isOnProPlan,
        })}
        renderContent={() => (
          <>
            {!isOnProPlan ? (
              <div className='flex flex-col gap-1 text-bedrock-black text-bedrock-p'>
                {plans && plans.enterprise && plans.enterprise.map((plan) => {
                  const planHasQuota = !!plan.quota;

                  if (planHasQuota) {
                    return (
                      <a
                        href={ContactUsCalendly}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={classNames('cursor-pointer', {
                          'text-bedrock-p': activeProInterval !== 'quarter',
                          'text-bedrock-p-strong': activeProInterval === 'quarter',
                        })}
                        onClick={() => {
                          setActiveProInterval('quarter');
                        }}
                      >
                        {plan.label}
                      </a>
                    );
                  }

                  return (
                    <span
                      className={classNames(
                        'cursor-pointer',
                        activeProInterval === plan.payment_interval ? 'text-bedrock-p-strong' : 'opacity-80'
                      )}
                      onClick={() => {
                        onSubcribeIntervalChange(plan.payment_interval);
                      }}
                    >
                      {plan.label}
                    </span>
                  );
                })}
              </div>
            ) : 
            subscriptionPlanType==='starter' || subscriptionPlanType==='pro'?
            <></>
            :
            (
              <>
                <div className='mb-6'>
                  <DashboardSettingsBillingSubscriptionCurrentPlan subTitle={currentPlanTextByPaymentInterval} />
                </div>
                {renderCurrentPlan()}
              </>
            )}
          </>
        )}
        renderFooter={() => (
          <>
            {canUpgrade && (
              <div className='flex justify-center mt-auto'>
                <button
                  type='submit'
                  disabled={activeProInterval === 'quarter'}
                  className={classNames('disabled:opacity-50 btn-bedrock-primary', {
                    'pointer-events-none': activeProInterval === 'quarter',
                  })}
                  onClick={() => {
                    if (activeProInterval === 'quarter') {
                      return;
                    }
                    onUpgradeClick('enterprise',activeProInterval);
                  }}
                >
                  Upgrade
                </button>
              </div>
            )}
            {canDowngrade && subscriptionPlanType==='enterprise' && (
              <div className='flex justify-center mt-auto gap-2'>
                <button
                  disabled={isPaused}
                  type='submit'
                  className='btn-bedrock-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={onDowngradeToFreeClick}
                >
                  Cancel subscription
                </button>
                <a href={STRIPE_BILLING_PORTAL_URL} target='_blank' rel='noopener noreferrer'>
                  <button className='btn-bedrock-secondary'>Update billing</button>
                </a>
              </div>
            )}
          </>
        )}
      />
    </div>

    </div>
  );
};
